import { dismissOpenOverlays } from "./dismissOpenOverlays";

/**
 * Close any open Select dropdown / Dropdown menu / DatePicker panel /
 * Cascader / Popover / Popconfirm when the page scrolls.
 *
 * None of these have close-on-scroll behaviour built in - an overlay opened
 * near the top of a long page stays open and simply follows its trigger, so
 * it ends up floating over unrelated content (or the trigger itself) once the
 * page has scrolled past it. All of them are portal-rendered outside the
 * scrolling container, so they never move or unmount on their own.
 *
 * This is handled once here rather than by rewiring every Select/Dropdown/
 * Popconfirm call site (there are hundreds) into a controlled `open` /
 * `onOpenChange` pair. See dismissOpenOverlays for how the actual dismiss
 * works and why it's safe for the rest of the app.
 *
 * Hover-triggered overlays (Tooltip) are untouched: rc-trigger only registers
 * that document listener when the popup is click-to-hide.
 */

// Every one of these is built on rc-trigger, which stays mounted while closed
// and marks itself with a `{prefix}-hidden` class rather than unmounting -
// Popconfirm and Popover both nest an `ant-popover` internally for this, the
// rest use their own class name.
const OPEN_OVERLAY = [
  ".ant-popconfirm:not(.ant-popover-hidden)",
  ".ant-popover:not(.ant-popover-hidden)",
  ".ant-select-dropdown:not(.ant-select-dropdown-hidden)",
  ".ant-dropdown:not(.ant-dropdown-hidden)",
  ".ant-picker-dropdown:not(.ant-picker-dropdown-hidden)",
  ".ant-cascader-dropdown:not(.ant-cascader-dropdown-hidden)",
].join(",");

// Scrolling INSIDE one of these is the overlay doing its own job - a long
// Select list, a date panel, or the Popconfirm itself - not the page moving
// underneath it, so it must not trigger a dismiss.
const SELF_SCROLLING_OVERLAYS = [
  ".ant-popconfirm",
  ".ant-popover",
  ".ant-select-dropdown",
  ".ant-picker-dropdown",
  ".ant-cascader-dropdown",
  ".ant-dropdown",
  ".ant-tooltip",
].join(",");

/** Exported for testing: should this scroll event dismiss open overlays? */
export const shouldDismiss = (target, hasOpenOverlay) => {
  if (!hasOpenOverlay) return false;
  // `target` is `document` for a page-level scroll, which is not an Element.
  if (target && typeof target.closest === "function" && target.closest(SELF_SCROLLING_OVERLAYS)) {
    return false;
  }
  return true;
};

const handleScroll = (event) => {
  const hasOpenOverlay = Boolean(document.querySelector(OPEN_OVERLAY));
  if (!shouldDismiss(event.target, hasOpenOverlay)) return;

  dismissOpenOverlays();
};

/**
 * Start listening. Capture phase, because `scroll` does not bubble: a listener
 * on window alone would miss scrolling inside a table body, a modal, or any
 * other overflow container.
 *
 * @returns {Function} unsubscribe
 */
const closeOverlaysOnScroll = () => {
  window.addEventListener("scroll", handleScroll, true);
  return () => window.removeEventListener("scroll", handleScroll, true);
};

export default closeOverlaysOnScroll;
